import type { ISODate, MemberId } from '../contract.js';
import { copy } from '../copy.js';
import { compareMonths, dateLong, daysInMonth, monthFromKey, monthKey, monthOf, partsOf } from '../dates.js';
import { cCancel } from './compose.js';
import { earliestMonthKey, entriesOf, memberOf, todayMonthKey } from './lookup.js';
import { backFromProfile, closeDay, closePerson, fadeTo, isOpenDay, jumpDay, openDay, openPerson, openProfile } from './nav.js';
import { peCancel } from './profileEdit.js';
import { uiError } from './store.js';
import type { AppStore } from './store.js';
import type { AppState, Patch } from './types.js';

/**
 * Places: the pages a page address names (owner, 4 October 2026: refreshing stays on the page, Back and
 * Forward walk through where you were, a link opens the page). The UI never reads the address: the web
 * layer turns an address into a place and asks to go there, and turns `placeOf` back into an address.
 *
 * - `home`: the calendar on today's month; `month`: the calendar on another month ('YYYY-MM').
 * - `day`: everyone who shared on a day; `post`: one member's post that day, by handle.
 * - `profile`: a member's profile, by handle (lower case, without the "@").
 */
export type Place =
  | { readonly kind: 'home' }
  | { readonly kind: 'month'; readonly month: string }
  | { readonly kind: 'day'; readonly date: ISODate }
  | { readonly kind: 'post'; readonly date: ISODate; readonly handle: string }
  | { readonly kind: 'profile'; readonly handle: string };

/** Writing your post or editing your profile: the address stays on the page they were opened from. */
export const isEditing = (st: AppState): boolean => st.view === 'compose' || !!st.pe;

/**
 * The page on screen, or null when there is none to name: before the app is ready, in the composer, and
 * between two pages (the cross-fade, a day closing into its tile, a post closing into its square). A page
 * that is still arriving (a day opening, a profile popping in) is already that page.
 */
export function placeOf(st: AppState): Place | null {
  if (st.boot !== 'ready' || !st.session) return null;
  if (st.lv || st.cd === 'out-pre' || st.cd === 'out' || st.pp === 'out') return null;
  if (st.view === 'cal') return st.month === todayMonthKey(st) ? { kind: 'home' } : { kind: 'month', month: st.month };
  if (st.view === 'day') return { kind: 'day', date: st.date };
  if (st.view === 'person') {
    const handle = memberOf(st, st.person).handle;
    return handle ? { kind: 'post', date: st.date, handle } : { kind: 'day', date: st.date };
  }
  if (st.view === 'profile') {
    const handle = memberOf(st, st.profileOf).handle;
    return handle ? { kind: 'profile', handle } : null;
  }
  return null;
}

/** Back or Forward while writing or editing: the same as Cancel, which asks first when something changed. */
export function leaveEditor(s: AppStore): void {
  const st = s.getState();
  if (st.view === 'compose') cCancel(s);
  else if (st.pe) peCancel(s);
}

const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
/** A calendar month the app can show: from the earliest month to today's. */
function monthOpen(st: AppState, key: string): boolean {
  if (!MONTH.test(key)) return false;
  const ym = monthFromKey(key);
  return compareMonths(ym, monthFromKey(earliestMonthKey(st))) >= 0 && compareMonths(ym, monthFromKey(todayMonthKey(st))) <= 0;
}
/** A real date that can be opened: from the first day of the earliest month to today. */
function dayOpen(st: AppState, date: ISODate): boolean {
  if (!DAY.test(date)) return false;
  const p = partsOf(date);
  return p.month >= 1 && p.month <= 12 && p.day >= 1 && p.day <= daysInMonth({ year: p.year, month: p.month }) && isOpenDay(st, date);
}

/** What every jump clears: the phases of a choreography and whatever was open on the screen. */
const SETTLED: Patch = { cd: null, pp: null, swap: 'still', pfx: null, hover: null, hoverP: null, tray: null, sel: null, txt: null, play: null, slide: 0 };

/** Straight to a page: the shared cross-fade when animating, at once otherwise (a link opened, a reload). */
function jump(s: AppStore, patch: Patch, animate: boolean): void {
  if (animate) { fadeTo(s, { ...SETTLED, ...patch }); return; }
  s.clearLater();
  s.stopPlayback();
  s.set({ ...SETTLED, ...patch, lv: false, lvKeep: false });
}
/** While a link waits for its data, the stage stays empty instead of showing the calendar first. */
function hide(s: AppStore): void {
  s.clearLater();
  s.set({ lv: true, lvKeep: false });
}

function toMonth(s: AppStore, key: string, steps: boolean): void {
  const st = s.getState();
  void s.loadMonth(key);
  if (st.view === 'cal' && !st.cd && !st.lv) { if (st.month !== key) s.set({ month: key, hover: null }); return; }
  if (steps && (st.view === 'day' || st.view === 'person') && monthKey(monthOf(st.date)) === key) { closeDay(s); return; }
  if (steps && st.view === 'profile' && st.ret === 'cal' && st.month === key) { backFromProfile(s); return; }
  jump(s, { view: 'cal', month: key, person: null }, steps);
}

function toDay(s: AppStore, date: ISODate, steps: boolean): void {
  const st = s.getState();
  if (st.view === 'day' && st.date === date && !st.cd && !st.lv) return;
  if (steps && st.view === 'cal' && st.month === monthKey(monthOf(date))) { openDay(s, date); return; }
  if (steps && st.view === 'person' && st.date === date) { closePerson(s); return; }
  if (steps && st.view === 'profile' && st.ret === 'day' && st.date === date) { backFromProfile(s); return; }
  if (steps && st.view === 'day') { jumpDay(s, date); return; }
  void s.loadDay(date);
  jump(s, { view: 'day', date, month: monthKey(monthOf(date)), person: null }, steps);
}

function toPost(s: AppStore, date: ISODate, id: MemberId, steps: boolean): void {
  const st = s.getState();
  if (st.view === 'person' && st.date === date && !st.lv && st.pp !== 'out') {
    if (st.person === id) return;
    // Another post of the same day: it slides over, as with the person arrows.
    const entries = entriesOf(st, date), from = entries.findIndex((entry) => entry.memberId === st.person), to = entries.findIndex((entry) => entry.memberId === id);
    s.stopPlayback();
    s.set({ person: id, cyc: st.cyc + 1, slide: to >= 0 && to < from ? -1 : 1, play: null });
    return;
  }
  if (steps && st.view === 'day' && st.date === date && !st.cd) { openPerson(s, id); return; }
  if (steps && st.view === 'profile' && st.ret === 'person' && st.person === id && st.date === date) { backFromProfile(s); return; }
  void s.loadDay(date);
  jump(s, { view: 'person', date, month: monthKey(monthOf(date)), person: id }, steps);
}

function toProfile(s: AppStore, id: MemberId, steps: boolean): void {
  const st = s.getState();
  if (st.view === 'profile' && st.profileOf === id && !st.lv) return;
  if (steps && !st.lv) { openProfile(s, id, st.view === 'day' || st.view === 'person' ? st.view : 'cal'); return; }
  void s.loadProfile(id);
  jump(s, { view: 'profile', profileOf: id, ret: 'cal', pe: null }, false);
}

/** The author of a post that day by handle: their id, null when nobody with that handle shared, undefined until the day is read. */
function authorOn(st: AppState, date: ISODate, handle: string): MemberId | null | undefined {
  const day = st.days[date];
  if (!day || day.status === 'loading' || !day.entries) return day?.status === 'error' ? null : undefined;
  return entriesOf(st, date).find((entry) => (entry.author.handle ?? memberOf(st, entry.memberId).handle) === handle)?.memberId ?? null;
}

/** A member by handle among the people the app already knows: their id, or undefined. */
function knownMember(st: AppState, handle: string): MemberId | undefined {
  if (st.session?.me.handle === handle) return st.session.me.id;
  return [...Object.values(st.people), ...(st.session?.members ?? [])].find((member) => member.handle === handle)?.id;
}

const runs = new WeakMap<AppStore, number>();

/**
 * Go to a place. `animate`: Back and Forward, with the app's own steps where one leads there (a day opening
 * from its tile, a post closing into its square…) and the cross-fade otherwise; without it, the page simply
 * shows (a link opened or a reload). A place the app cannot show goes to today's month: a month or day out of
 * range, a handle nobody has (with a toast), a post its author did not share that day (its day, with a toast).
 * The newest call wins; nothing happens in the composer or while your profile is being edited.
 */
export async function goToPlace(s: AppStore, place: Place, animate: boolean): Promise<void> {
  const run = (runs.get(s) ?? 0) + 1;
  runs.set(s, run);
  const live = () => runs.get(s) === run;
  const start = s.getState();
  if (start.boot !== 'ready' || !start.session || isEditing(start)) return;
  // In the middle of a choreography its steps cannot start: the page cross-fades instead.
  const steps = animate && !start.lv && !start.cd && !start.pp;
  const home = () => toMonth(s, todayMonthKey(s.getState()), steps);
  try {
    if (place.kind === 'home' || place.kind === 'month') {
      const key = place.kind === 'home' ? todayMonthKey(start) : place.month;
      if (monthOpen(start, key)) toMonth(s, key, steps); else home();
      return;
    }
    if (place.kind === 'day') {
      if (dayOpen(start, place.date)) toDay(s, place.date, steps); else home();
      return;
    }
    if (place.kind === 'post') {
      if (!dayOpen(start, place.date)) { home(); return; }
      let id = authorOn(start, place.date, place.handle);
      if (id === undefined) {
        if (!steps) hide(s);
        await s.loadDay(place.date);
        if (!live()) return;
        id = authorOn(s.getState(), place.date, place.handle);
      }
      if (id) { toPost(s, place.date, id, steps); return; }
      if (s.getState().days[place.date]?.status === 'loaded') s.flash(copy.toast.noPostThatDay(place.handle, dateLong(place.date)));
      toDay(s, place.date, steps);
      return;
    }
    let id: MemberId | null | undefined = knownMember(start, place.handle);
    if (id === undefined) {
      if (!s.data.findMember) id = null;
      else {
        if (!steps) hide(s);
        const found = await s.data.findMember(place.handle);
        if (!live()) return;
        if (found) s.meet(found);
        id = found?.id ?? null;
      }
    }
    if (id) { toProfile(s, id, steps); return; }
    s.flash(copy.toast.noSuchHandle(place.handle));
    home();
  } catch (reason) {
    if (!live()) return;
    s.report(uiError(reason));
    s.flash(copy.toast.pageFailed);
    home();
  }
}
